// Unpublished Work © 2020-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import TileCategory from 'Ui/components/tile-editor/tile-category';
import {categories} from 'Ui/components/common/tile-categories';
import {fetchEffectData} from 'Utils/react-utils';
import {isTileVisible} from 'Utils/tile-utils';
import {sortBy} from 'lodash';
import {getTiles} from 'Services/user-view-service';
import {connect} from 'react-redux';
import {List, Typography} from '@deere/isg.component-library';

function TileEditor(props) {
    const {
        activeTiles,
        setActiveTiles,
        translations,
        isgToggles,
        isMigrated,
        myJdPermissions
    } = props;

    const initialActiveTiles = React.useRef(activeTiles);

    const onChange = React.useCallback((event) => {
        setActiveTiles({
            ...activeTiles,
            [event.target.id]: event.target.checked
        });
    }, [activeTiles]);

    const userAuth = React.useMemo(() => ({
        isMigrated,
        myJdPermissions,
        isgToggles
    }), [isMigrated, myJdPermissions, isgToggles]);

    const [tilesByCategory, setTilesByCategoryId] = React.useState({});

    React.useEffect(() => fetchEffectData(async () => {
        const tiles = await getTiles();
        const tileMap = tiles.filter(({id}) => initialActiveTiles.current[id] || isTileVisible(id, userAuth)
        ).reduce((map, tile) => {
            map[tile.id] = tile;

            return map;
        }, {});

        setTilesByCategoryId(categories.reduce((map, {
            name, tiles: tileIds
        }) => {
            map[name] = sortBy(
                tileIds.map((id) => tileMap[id]).filter(Boolean),
                'name'
            );

            return map;
        }, {}));
    }), [initialActiveTiles, userAuth]);

    return (
        <div
            className='tile-editor'
            data-testid='tile-editor'
        >
            <List
                subheader={
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            marginBottom: '16px'
                        }}
                    >{translations.SELECT_TILES_FOR_NEW_DASHBOARD}</Typography>
                }
            >
                {
                    categories.map(({name}) => {
                        return (<TileCategory
                            activeTiles={activeTiles}
                            initialActiveTiles={initialActiveTiles.current}
                            key={name}
                            onChange={onChange}
                            tiles={tilesByCategory[name] || []}
                            translations={translations}
                            userAuth={userAuth}
                        />);
                    })
                }
            </List>
        </div>
    );
}

TileEditor.propTypes = {
    activeTiles: PropTypes.objectOfBool,
    isgToggles: PropTypes.object,
    isMigrated: PropTypes.bool,
    myJdPermissions: PropTypes.myJdPermissions,
    setActiveTiles: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isgToggles: state.account.isgToggles,
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions
    };
}

export default connect(mapStateToProps)(TileEditor);
